import React, { useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleUp } from "@fortawesome/free-solid-svg-icons";
import { GameClient } from "../gameClient";
import { Button, ExtraMarketButton } from "../styles/ButtonStyle";
import { PlayerConstants, UIConstants } from "../../../shared/constants";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { CircleProgress } from "./CircleProgress";
import { CostMarker, TierMarker } from "./TierMarker";
import { InfoLevel } from "../types/uiTypes";
import { Sound } from "../../../shared/sound";
import { Player } from "../../../shared/player";
import { TutorialStep } from "./TooltipInfo";

const maxLevelStyle = css`width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 24px;`

declare var gameClient: GameClient;

type ExperienceButtonProps = {
	experience: number,
	level: number,
	setPurchased: (value: boolean) => void,
	setHovered: (amount: number) => void,
	sendInfo: (text: string) => void,
	enabledButtons?: string
}

const calculateProgress = function (level: number, experience: number) {
	const previousLevelExperienceRequirement = PlayerConstants.ExperienceLevels[level - 2] || 0;
	const nextLevelExperienceRequirement = PlayerConstants.ExperienceLevels[level - 1];
	const num = experience - previousLevelExperienceRequirement;
	const dem = nextLevelExperienceRequirement - previousLevelExperienceRequirement;
	//console.log(experience, previousLevelExperienceRequirement, nextLevelExperienceRequirement, experience - previousLevelExperienceRequirement, num, dem);
	return 1 - num / dem;
}

export const ExperienceButton = (props: ExperienceButtonProps) => {
	const [experience, setExperience] = React.useState(props.experience);
	const [level, setLevel] = React.useState(props.level);
	const [progress, setProgress] = React.useState(calculateProgress(level, experience));
	const ref = React.useRef(experience);
	const refLevel = React.useRef(level);
	const refProgress = React.useRef(progress);
	const [expHover, setExpHover] = React.useState(false);

	const onProgressFull = useCallback(() => {
		refProgress.current = 1;
		setProgress(1);
	}, [progress]);

	React.useEffect(() => {
		ref.current = props.experience;
		setExperience(ref.current);
		const progress = calculateProgress(level, ref.current);
		refProgress.current = progress;
		setProgress(progress);
	}, [props.experience]);

	React.useEffect(() => {
		refLevel.current = props.level;
		setLevel(refLevel.current);
		props.setPurchased(true);
	}, [props.level]);

	const buyExperience = useCallback(() => {
		gameClient.playUISound(Sound.ButtonClick);
		gameClient.sendBuyExperience()
	}, []);

	const hovered = () => {
		setExpHover(true);
		props.setHovered(PlayerConstants.ExperienceCost);
		if (gameClient) {
			gameClient.playUISound(Sound.Menu);
			props.sendInfo(`Experience: Pay ${PlayerConstants.ExperienceCost} to get ${PlayerConstants.ExperienceGain} experience, new levels give access to different Immune Cells`);
		}
	}
	const clear = () => {
		setExpHover(false);
		props.setHovered(0);
		props.sendInfo('');
	}

	let disabled = false;
	if (props.enabledButtons === undefined) {
		disabled = level >= PlayerConstants.MaxLevel;
	} else {
		disabled = props.enabledButtons !== "experience";
	}

	return (
		<ExtraMarketButton
			tabIndex={-1}
			onClick={buyExperience}
			position={"left"}
			onMouseEnter={hovered}
			onMouseLeave={clear}
			disabled={disabled}
		>
			{
				level < PlayerConstants.MaxLevel ?
					<FontAwesomeIcon icon={faAngleDoubleUp} size="2x" />
					:
					<div css={maxLevelStyle}>MAX</div>
			}
			{
				level < PlayerConstants.MaxLevel &&
				<CircleProgress
					name="experience"
					progress={refProgress}
					onProgressFull={onProgressFull}
					numTicks={PlayerConstants.ExperienceLevels[level - 1] - (level >= 2 ? PlayerConstants.ExperienceLevels[level - 2] : 0)} />
			}
			{level < PlayerConstants.MaxLevel && expHover && <div style={{
				position: "absolute",
				top: 0,
				left: "50%"
			}}>
				<CostMarker cost={PlayerConstants.ExperienceCost} />
			</div>}
		</ExtraMarketButton>
	)
}
